import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import useMatchMedia from 'hooks/useMatchMedia';

const SIDE_OFFSET = 8;
const ARROW_EDGE_OFFSET = 12;

type Params = {
  popupRef: RefObject<HTMLDivElement>;
  triggerRef: RefObject<HTMLDivElement>;
  contentRef: RefObject<HTMLDivElement>;
  isMounted: boolean;
  preferUpwards?: boolean;
  centerOnVisibleTrigger?: boolean;
  setIsUpwards: (value: boolean) => void;
  onPointerLeave?: () => void;
};

const usePositioning = ({
  popupRef,
  triggerRef,
  contentRef,
  isMounted,
  preferUpwards,
  centerOnVisibleTrigger,
  setIsUpwards,
  onPointerLeave,
}: Params) => {
  const [isPositioned, setIsPositioned] = useState(false);

  const isTouchDevice = useMatchMedia('isTouchDevice');

  const rafRef = useRef<number>(null);

  const updatePosition = useCallback(
    (retry = true) => {
      const trigger = triggerRef.current;
      const content = contentRef.current;
      if (!trigger || !content) return;

      const wrapper = content.querySelector(
        '.resolve-popup__content__content-wrapper',
      ) as HTMLDivElement | null;

      if (wrapper) wrapper.style.removeProperty('--popup-arrow-left');

      const { width: contentWidth, height: contentHeight } =
        content.getBoundingClientRect();

      if (!contentWidth || !contentHeight) {
        if (retry)
          rafRef.current = requestAnimationFrame(() => updatePosition(false));
        return;
      }

      const triggerRect = trigger.getBoundingClientRect();
      const { left: triggerLeft, right: triggerRight } = centerOnVisibleTrigger
        ? getVisibleHorizontalBounds(trigger)
        : triggerRect;
      const triggerCenterX = (triggerLeft + triggerRight) / 2;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - triggerRect.bottom;
      const isUpwards = preferUpwards
        ? triggerRect.top >= contentHeight + SIDE_OFFSET
        : spaceBelow < contentHeight && triggerRect.top > spaceBelow;

      setIsUpwards(isUpwards);

      const top = isUpwards
        ? triggerRect.top + window.scrollY - contentHeight
        : triggerRect.bottom + window.scrollY;

      const availableHeight =
        (isUpwards ? triggerRect.top : spaceBelow) - ARROW_EDGE_OFFSET;

      content.style.setProperty(
        '--popup-max-height',
        `${Math.min(Math.max(availableHeight, 0), viewportHeight)}px`,
      );

      const naturalLeft = triggerCenterX + window.scrollX - contentWidth / 2;
      const clampedLeft = Math.min(
        Math.max(naturalLeft, SIDE_OFFSET + window.scrollX),
        window.scrollX + viewportWidth - contentWidth - SIDE_OFFSET,
      );

      if (wrapper && clampedLeft !== naturalLeft) {
        const arrowOffsetPx = Math.min(
          Math.max(
            triggerCenterX - (clampedLeft - window.scrollX),
            ARROW_EDGE_OFFSET,
          ),
          contentWidth - ARROW_EDGE_OFFSET,
        );
        const arrowPercent = (arrowOffsetPx / contentWidth) * 100;
        wrapper.style.setProperty('--popup-arrow-left', `${arrowPercent}%`);
      }

      content.style.top = `${top}px`;
      content.style.left = `${clampedLeft}px`;
      content.style.visibility = 'visible';

      setIsPositioned(true);
    },
    [
      triggerRef,
      contentRef,
      preferUpwards,
      centerOnVisibleTrigger,
      setIsUpwards,
    ],
  );

  useEffect(() => {
    if (!isMounted) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setIsPositioned(false);
      return;
    }

    rafRef.current = requestAnimationFrame(() => updatePosition());

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isMounted, updatePosition]);

  useEffect(() => {
    if (!isMounted) return;

    const reposition = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => updatePosition());
    };

    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);

    const resizeObserver = new ResizeObserver(reposition);

    if (triggerRef.current) resizeObserver.observe(triggerRef.current);
    if (contentRef.current) resizeObserver.observe(contentRef.current);

    let lastRect: DOMRect | undefined;
    let watchId: number;

    const watchTrigger = () => {
      const popup = popupRef.current;
      const trigger = triggerRef.current;
      const content = contentRef.current;

      if (popup && trigger && content) {
        // a reorder moves the trigger without resizing or scrolling it, which
        // neither the observer nor the listeners above report
        const rect = trigger.getBoundingClientRect();

        if (
          lastRect &&
          (rect.top !== lastRect.top || rect.left !== lastRect.left)
        )
          reposition();

        lastRect = rect;

        // a stationary pointer gets no mouseleave when the trigger slides out
        // from under it, so :hover is the only thing left telling the truth
        if (
          !isTouchDevice &&
          !popup.matches(':hover') &&
          !content.matches(':hover')
        )
          onPointerLeave?.();
      }

      watchId = requestAnimationFrame(watchTrigger);
    };

    watchId = requestAnimationFrame(watchTrigger);

    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      resizeObserver.disconnect();
      cancelAnimationFrame(watchId);
    };
  }, [
    isMounted,
    popupRef,
    triggerRef,
    contentRef,
    updatePosition,
    onPointerLeave,
    isTouchDevice,
  ]);

  return { isPositioned };
};

const getVisibleHorizontalBounds = (element: HTMLElement) => {
  let { left, right } = element.getBoundingClientRect();
  let parent = element.parentElement;

  while (parent) {
    if (getComputedStyle(parent).overflowX !== 'visible') {
      const parentRect = parent.getBoundingClientRect();

      left = Math.max(left, parentRect.left);
      right = Math.min(right, parentRect.right);
    }

    parent = parent.parentElement;
  }

  return { left, right };
};

export default usePositioning;
